import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateAllContactAutomationsQueries } from './queries/useContactAutomationsQuery';

/**
 * Mutation hook to start (activate) an automation template for all eligible contacts.
 *
 * "Starting" an automation means activating it so that eligible contacts begin entering
 * and progressing through the automation's steps. This is different from publishing
 * (which commits draft changes) - starting activates an already-published automation.
 *
 * What happens when you start an automation:
 * 1. All contacts matching the trigger criteria become eligible
 * 2. Eligible contacts enter the automation and begin at the first step
 * 3. Steps execute according to their schedule configuration
 * 4. New contacts matching the trigger continue to enter (if trigger is ongoing)
 *
 * Use this mutation for:
 * - Activating a newly created automation
 * - Restarting a paused/stopped automation
 * - Batch processing all eligible contacts
 *
 * This is different from useStartAutomationForContactMutation which starts
 * the automation for a single specific contact (useful for testing or manual entry).
 *
 * @param options - Mutation configuration
 * @param options.impersonationContext - Optional impersonation context for admin operations
 * @param options.onSuccess - Callback invoked after successful start
 * @param options.onError - Callback invoked on error
 * @returns React Query mutation with mutateAsync function
 *
 * @example
 * ```typescript
 * const startMutation = useStartAutomationMutation({
 *   onSuccess: () => {
 *     invalidateAllAutomationTemplateQueries();
 *     showToast('Automation started successfully!');
 *     navigate('/automations');
 *   },
 *   onError: (error) => {
 *     showErrorToast(error.systemMessage || 'Failed to start automation');
 *   }
 * });
 *
 * // In a start button:
 * <Button
 *   onClick={() => startMutation.mutateAsync({ automationId: template.id })}
 *   loading={startMutation.isPending}
 * >
 *   Start Automation
 * </Button>
 * ```
 *
 * @example
 * ```typescript
 * // With confirmation dialog:
 * const { data: eligibleContacts } = useAutomationTemplateEligibleContactsQuery({
 *   templateId
 * });
 *
 * const handleStart = async () => {
 *   const confirmed = await showConfirmDialog(
 *     `Start automation for ${eligibleContacts?.totalCount} contacts?`
 *   );
 *   if (confirmed) {
 *     await startMutation.mutateAsync({ automationId: templateId });
 *   }
 * };
 * ```
 */
export function useStartAutomationMutation({
	impersonationContext,
	onSuccess,
	onError,
}: {
	impersonationContext?: Api.IImpersonationContext;
	onSuccess?: (data: Api.IOperationResultNoValue) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
}) {
	const userSession = useUserSession();

	return useMutation({
		mutationKey: ['startAutomation'],
		onSuccess: (data: Api.IOperationResultNoValue) => {
			invalidateAllContactAutomationsQueries();
			onSuccess?.(data);
		},
		onError,
		mutationFn: ({ automationId }: { automationId: string }) => {
			return userSession.webServiceHelper.callAsync<Api.IOperationResultNoValue>(
				Api.ImpersonationBroker.composeApiUrl({
					impersonationContext,
					urlPath: `Automation/${automationId}/Start`,
				}),
				'POST',
				null
			);
		},
	});
}
